/**
 * `AccessLevelService` — `/api/v1/events/{eventId}/access-levels/*`.
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Уровень доступа — цифра, которая печатается на
 * бейдже и говорит человеку на входе, насколько глубоко пускают предъявителя.
 * Она не описывает ничего из того, что человек может сделать в приложении:
 * права по-прежнему описывают `auth.isAdmin` и `event.canManage`, и ни одна
 * проверка не имеет права смотреть сюда при рисовании кнопок. Слово «уровень»
 * тянет к иерархии полномочий сильнее, чем «зона», — тем важнее сказать это
 * прямо здесь, в файле, который сам называется `access-levels`.
 *
 * ⚠️ Уровни и зоны — ДВА НЕЗАВИСИМЫХ справочника, а не старый и новый. Зона
 * отвечает «куда пускают», уровень — «насколько глубоко»; у роли бывает и то,
 * и другое, и ни одного из двух.
 *
 * Справочник **локален** для мероприятия: шкала «1..5» одного соревнования
 * ничего не значит на другом.
 *
 * ПРАВА те же, что и у зон: admin и owner наполняют, viewer читает, не
 * участник получает 404 `EVENT_NOT_FOUND`.
 */

import { http } from '@/lib/http'
import type {
  CreateAccessLevelRequest,
  CreateAccessLevelResponse,
  DeleteAccessLevelResponse,
  ListAccessLevelsResponse,
  UpdateAccessLevelRequest,
  UpdateAccessLevelResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/access-levels${suffix}`
}

export const accessLevelsApi = {
  /**
   * `GET .../access-levels` — весь справочник, **по возрастанию номера**.
   * Читает любой участник мероприятия.
   *
   * Пагинации нет намеренно: это десятки записей, из которых собирают набор
   * отметок на бейдже, а не лента.
   *
   * ⚠️ Порядок задаёт сервер. Пересортировывать на клиенте нельзя — и особенно
   * нельзя сортировать как строки: уровень 10 между 1 и 2 делает шкалу
   * нечитаемой.
   */
  async list(eventId: string, signal?: AbortSignal): Promise<ListAccessLevelsResponse> {
    const { data } = await http.get<ListAccessLevelsResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST .../access-levels` — admin или owner.
   *
   * `409 ACCESS_LEVEL_ALREADY_EXISTS` с `metadata.number` — номер занят. Ошибку
   * вешают на поле НОМЕРА: описание к конфликту отношения не имеет.
   *
   * ⚠️ `number` обязан быть числом. Из `<input type="number">` значение
   * приходит строкой, и `"7"` в теле запроса вернётся `VALIDATION_FAILED` с
   * `metadata.field = "number"`. Превращение делает вызывающий — здесь тело
   * уже контрактное.
   */
  async create(
    eventId: string,
    body: CreateAccessLevelRequest,
  ): Promise<CreateAccessLevelResponse> {
    const { data } = await http.post<CreateAccessLevelResponse>(path(eventId), body)
    return data
  },

  /**
   * `PUT .../access-levels/{id}` — оба поля разом.
   *
   * PUT, а не PATCH: полей ровно два, оба всегда есть в форме. Пустое описание
   * здесь СТИРАЕТ описание, а не означает «не менять» — иначе очистить его
   * было бы нечем.
   */
  async update(
    eventId: string,
    id: string,
    body: UpdateAccessLevelRequest,
  ): Promise<UpdateAccessLevelResponse> {
    const { data } = await http.put<UpdateAccessLevelResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
      body,
    )
    return data
  },

  /**
   * `DELETE .../access-levels/{id}` — жёсткое удаление.
   *
   * ⚠️ Привязки к ролям уходят каскадом НА СЕРВЕРЕ: у каждой роли, которая
   * выдавала этот уровень, он молча пропадёт. Клиент об этом не узнает сам —
   * стор ролей продолжит держать id несуществующего уровня, пока его не
   * перечитают. Поэтому вызывающий обязан (а) назвать эти роли в
   * подтверждении и (б) после удаления перечитать справочник ролей.
   */
  async remove(eventId: string, id: string): Promise<DeleteAccessLevelResponse> {
    const { data } = await http.delete<DeleteAccessLevelResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
    )
    return data
  },
}
